/**
 * Categorical palette for group colours, validated with the data-viz
 * validator against both app surfaces (#ffffff light, #12151d dark):
 * every slot passes the lightness band, chroma floor, adjacent CVD ΔE
 * and normal-vision ΔE gates. Three light slots sit below 3:1 contrast,
 * which the relief rule covers – every bar and swatch in this app is
 * directly labelled, so colour never carries meaning on its own.
 *
 * Groups store the light hex; `chartColor` swaps in the dark step so a
 * group keeps its identity in both modes.
 */
export interface PaletteSlot {
  name: string;
  light: string;
  dark: string;
}

export const PALETTE: PaletteSlot[] = [
  { name: 'Blau', light: '#2a78d6', dark: '#3987e5' },
  { name: 'Orange', light: '#eb6834', dark: '#d95926' },
  { name: 'Aqua', light: '#1baf7a', dark: '#199e70' },
  { name: 'Gelb', light: '#eda100', dark: '#c98500' },
  { name: 'Magenta', light: '#e87ba4', dark: '#d55181' },
  { name: 'Grün', light: '#008300', dark: '#008300' },
  { name: 'Violett', light: '#4a3aa7', dark: '#9085e9' },
  { name: 'Rot', light: '#e34948', dark: '#e66767' },
];

const DARK_BY_LIGHT = new Map(PALETTE.map((slot) => [slot.light.toLowerCase(), slot.dark]));

/** The colour a group's mark should use in the given mode. */
export function chartColor(hex: string, isDark: boolean): string {
  if (!isDark) return hex;
  return DARK_BY_LIGHT.get(hex.toLowerCase()) ?? hex;
}

/** Cycles through the palette so a new group does not repeat the last one. */
export function nextGroupColor(usedColors: readonly string[]): string {
  const used = new Set(usedColors.map((color) => color.toLowerCase()));
  const free = PALETTE.find((slot) => !used.has(slot.light.toLowerCase()));
  return (free ?? PALETTE[usedColors.length % PALETTE.length]!).light;
}

/** Neutral used for the "Ohne Gruppe" bucket – deliberately not a palette slot. */
export const UNGROUPED_COLOR = '#94a3b8';
